import { atom, atomFamily, selector, selectorFamily, useRecoilValue, useSetRecoilState } from 'recoil';
import type { Identifiable, MissionEvent } from './api';
import { statePrefix } from './config';
import type { FullMission, SectionWithResources } from './types';

const groupMissionsAtom = atom<Record<string, Array<FullMission>>>({
  key: `${statePrefix}:groupMissions`,
  default: {},
});

const missionEventsAtom = atomFamily<Array<MissionEvent & Identifiable>, string>({ key: `${statePrefix}:missionEvents`, default: [] });

export const missionsSelector = selector<Array<FullMission>>({
  key: `${statePrefix}:missions`,
  get: ({ get }) => {
    const missions = get(groupMissionsAtom);
    const byMissionId = Object.values(missions)
      .flatMap((ts) => ts)
      .reduce((a, t) => ({ ...a, [t.id]: a[t.id] ?? t }), {} as Record<string, FullMission>);
    return Object.values(byMissionId).sort((a, b) => b.createdAt - a.createdAt);
  },
});

const missionSelector = selectorFamily<FullMission | undefined, string>({
  key: `${statePrefix}:mission`,
  get:
    (missionId) =>
    ({ get }) => {
      const missions = get(missionsSelector);
      return missions.find((m) => m.id === missionId);
    },
});

export type ActiveMissionId = {
  missionId: string;
  sectionId?: string;
  pinnedSectionId?: string;
};

export const activeMissionAtom = atom<ActiveMissionId | undefined>({
  key: `${statePrefix}:activeMission:atom`,
  default: undefined,
});

export type ActiveMission = {
  mission: FullMission;
  section?: SectionWithResources;
  pinnedSection?: SectionWithResources;
};

const activeMissionSelector = selector<ActiveMission | undefined>({
  key: `${statePrefix}:activeMission:selector`,
  get: ({ get }) => {
    const active = get(activeMissionAtom);
    if (!active) return;
    const tableaus = get(missionsSelector);
    const mission = tableaus.find((e) => e.id === active.missionId);
    if (!mission) return;
    const section = active.sectionId ? mission.sections.find((s) => s.id === active.sectionId) : undefined;
    const pinnedSection = active.pinnedSectionId ? mission.sections.find((s) => s.id === active.pinnedSectionId) : undefined;
    return { mission, section, pinnedSection };
  },
});

export function useMissions() {
  return useRecoilValue(missionsSelector);
}

export function useMission(missionId: string) {
  return useRecoilValue(missionSelector(missionId));
}

export function useSetMissions() {
  return useSetRecoilState(groupMissionsAtom);
}

export function useActiveMission(): ActiveMission | undefined {
  return useRecoilValue(activeMissionSelector);
}

export function useSetActiveMission() {
  return useSetRecoilState(activeMissionAtom);
}

export function useMissionEvents(missionId: string) {
  return useRecoilValue(missionEventsAtom(missionId));
}

export function useSetMissionEvents(missionId: string) {
  return useSetRecoilState(missionEventsAtom(missionId));
}
